import { useState } from "react";

import { InputError, UseCatNamesFormReturn } from "../types";
import { validate } from "../utils";

export function useCatNamesForm(initialNames: string[] = [ "" ]): UseCatNamesFormReturn {
  const [ catNames, setCatNames ] = useState<string[]>(initialNames.length ? initialNames : [ "" ]);
  const [ errors, setErrors ] = useState<Record<number, InputError>>({});
  const [ touchedIndexes, setTouchedIndexes ] = useState<ReadonlySet<number>>(new Set());

  const errorIndexes = new Set(
    Object.entries(errors)
      .filter(([ index ]) => touchedIndexes.has(Number(index)))
      .map(([ index ]) => Number(index)),
  );

  const updateName = (index: number, value: string) => {
    const newNames = [ ...catNames ];
    newNames[index] = value;
    setCatNames(newNames);

    const updatedTouched = new Set(touchedIndexes);
    updatedTouched.add(index);
    setTouchedIndexes(updatedTouched);

    setErrors(validate(newNames, updatedTouched));
  };

  const addName = () => {
    const newNames = [ ...catNames, "" ];
    setCatNames(newNames);
    setErrors(validate(newNames, touchedIndexes));
  };

  const deleteName = (index: number) => {
    const newNames = catNames.filter((_, i) => i !== index);
    setCatNames(newNames);

    const reindexedTouched = new Set<number>();
    touchedIndexes.forEach((i) => {
      if (i < index) { reindexedTouched.add(i); } else if (i > index) { reindexedTouched.add(i - 1); }
    });

    setTouchedIndexes(reindexedTouched);
    setErrors(validate(newNames, reindexedTouched));
  };

  const validateAll = (): boolean => {
    const allIndexes = new Set(catNames.map((_, i) => i));
    setTouchedIndexes(allIndexes);

    const newErrors = validate(catNames, allIndexes);
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const getFormattedCatEntries = () => catNames.map((name, index) => ({
    id: `${index + 1}`,
    cat: { name },
  }));

  return {
    catNames,
    errors,
    errorIndexes,
    updateName,
    addName,
    deleteName,
    validateAll,
    getFormattedCatEntries,
  };
}
